import AddPhotoAlternateOutlinedIcon from "@mui/icons-material/AddPhotoAlternateOutlined";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import { Button, Grid, Tooltip } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useFormik } from "formik";
import AsyncSelect from "../../../../../components/AsyncSelect";
import CustomDatePicker from "../../../../../components/CustomDatePicker";
import CustomSelect from "../../../../../components/CustomSelect";
import CustomTextArea from "../../../../../components/CustomTextArea";
import Flex from "../../../../../components/Flex";
import Input from "../../../../../components/Input";
import MaskMultiSelect from "../../../../../components/MaskMultiSelect";
import PatientSelect from "../../../../../components/PatientSelect";
import ProviderSelect from "../../../../../components/ProviderSelect";
import SideSheetDrawer from "../../../../../components/SidesheetDrawer";
import Text from "../../../../../components/Text";
import DropZone from "../../../../../components/app/DropZone";
import NewPatient from "../../../../../components/app/provider/scheduling/NewPatient";
import { useDebounce } from "../../../../../hooks/use-debounce";
import { DisclosureHost } from "../../../../../hooks/use-disclosure";
import { useQueryParams } from "../../../../../hooks/use-query-param";
import { useReduceDisclosure } from "../../../../../hooks/use-reduce-disclosure";
import { useSnackbar } from "../../../../../hooks/useSnackbar";
import useGetCataLogCode from "../../../../../queries/catalog/use-get-catalog-code";
import { useAddReferral } from "../../../../../queries/collaboration/referral/use-add-referral";
import { useUpdateReferral } from "../../../../../queries/collaboration/referral/use-update-referral";
import { useGetInsuranceByPatientId } from "../../../../../queries/insurance/use-get-patient-insurance-patient-uuid";

import { getStoredRealm } from "@/utils/auth-storage";
import { useAddPatient } from "../../../../../queries/use-add-patient";
import { useAppointmentsV2 } from "@/queries/patient-portal/forms/appointment/use-appointment-list-v2";
import { useSpecialtiesList } from "../../../../../queries/use-specialities-list";
import { formatDateTime } from "../../../../../utils/date-time-utility";
import {
  convertToBase64,
  DEF_PAGINATION,
  getUnNecessaryTransformData,
  getXtenantId,
  replaceUnderScoreAddSpace,
  showToastOnApiCall,
} from "../../../../../utils/utils";
import {
  NEW_REFERRAL_VALIDATION_SCHEMA,
  REFERRAL_INITIAL_VALUES,
  REFERRAL_PRIORITIES,
} from "../collaboration-referral-constant";
import NewReferringProvider from "./NewReferringProvider";

const UpsertReferral = ({
  disclosureHost,
  title,
  prefillData,
  referringOutFromEncounter = false,
  hideAddPatient = false,
}: {
  disclosureHost: DisclosureHost;
  title?: string;
  prefillData?: any;
  referringOutFromEncounter?: boolean;
  hideAddPatient?: boolean;
}) => {
  const referalParam = useQueryParams("activeReferral");
  const isReferralOut =
    referringOutFromEncounter || referalParam === "referral-out";

  const isReferralIn = referalParam === "referral-in";

  const { mutateAsync: updateReferral } = useUpdateReferral();
  const { mutateAsync: createReferral } = useAddReferral();

  const showSnackbar = useSnackbar();
  const queryClient = useQueryClient();
  const formik = useFormik({
    initialValues: prefillData || REFERRAL_INITIAL_VALUES,
    validationSchema: NEW_REFERRAL_VALIDATION_SCHEMA,
    onSubmit: handleSubmit,
    enableReinitialize: true,
  });

  // Editing an existing referral: the mandatory fields that identify it
  // (Patient, Referral From, Referral To and Referral Date) must not change
  // after creation, so they are locked in edit mode. The condition mirrors the
  // create-vs-update rule used in handleSubmit, so fields are locked exactly
  // when the form updates an existing referral.
  const isEditMode = Boolean(prefillData) && !referringOutFromEncounter;

  const getObj = (obj: { value: string; label: string }) =>
    obj ? { [obj.value]: obj.label } : undefined;
  // console.log("formik value ", formik.values);

  async function handleSubmit(values?: any) {
    const selectedPatient = getObj(values.patient);
    const referralFrom = getObj(values.referralFrom);
    const referralAppointment = getObj(values.referralAppointment);
    const referralSpecialities = getObj(values.referralSpecialities);
    // const patientInsurance = getObj(values.patientInsurance);

    const referralTo = getObj(values.referralTo);

    const payload = {
      referral: getUnNecessaryTransformData(["referralDiagnosisCode"], {
        ...values,
        patient: selectedPatient,
        referralFrom,
        referralAppointment,
        referralSpecialities,
        patientInsurance: values.patientInsurance
          ? {
              uuid: values.patientInsurance?.value,
            }
          : undefined,

        referralTo: referralTo,
        referralIn: isReferralIn,
      }),
      xTENANTID: getXtenantId(),
    };

    if (prefillData && !referringOutFromEncounter)
      showToastAndResetForm(updateReferral(payload));
    else showToastAndResetForm(createReferral(payload));
  }

  async function showToastAndResetForm(apiCall: Promise<any>) {
    showToastOnApiCall({
      apiCall,
      showSnackbar,
      onSave: () => {
        queryClient.invalidateQueries({
          queryKey: ["collaboration-referral-list"],
        });
        queryClient.invalidateQueries({
          queryKey: ["referral-by-id"],
        });
        closeAndResetForm();
      },
    });
  }

  function closeAndResetForm() {
    formik.resetForm();
    disclosureHost.close();
  }

  // console.log("active referral", isReferralOut);

  const [specialitySearch, setSpecialitySearch] = useDebounce<string>("");
  const [searchCodeKey, setSearchCodeKey] = useDebounce<string>("");
  const [newPatientDisclosure, newReferringProvider] = useReduceDisclosure(2);
  const client = useQueryClient();

  const {
    data: specialtiesList,
    isLoading: isLoadingSpeciality,
    isRefetching: isRefetchingSpeciality,
  } = useSpecialtiesList({
    searchString: specialitySearch,
    xTENANTID: getXtenantId(),
  });

  const { data: diagnosisList } = useGetCataLogCode({
    pagination: DEF_PAGINATION,
    searchKey: searchCodeKey,
    type: "ICD",
    active: true,
    archive: false,
  });

  const [searchAppointmentKey, setSearchAppointmentKey] =
    useDebounce<string>("");

  const {
    data: appointmentList,
    isFetching: isFetchingAppointmentList,
    isLoading: isLoadingAppointmentList,
  } = useAppointmentsV2({
    providerGroupId: getXtenantId(),
    pagination: DEF_PAGINATION,
    patientUuid: formik.values.patient?.value
      ? [formik.values.patient.value]
      : undefined,
    appointmentStatus: ["CONFIRMED", "PENDING"] as any, // note : PENDING is for scheduled appointments.
    searchKey: searchAppointmentKey,
    // The field only renders for a referral out, and the list is per patient.
    disabled:
      !formik.values.patient?.value ||
      !(referringOutFromEncounter || formik.values.referralAppointment),
  });

  // console.log("op ", referralProvidersList, providersList);

  const { mutateAsync: addPatientApi } = useAddPatient();

  async function addNewPatientHandler(values: any) {
    return addPatientApi({
      patient: { ...values, isReferral: true },
      xTENANTID: getStoredRealm() ?? "",
    }).then((res) => {
      client.invalidateQueries({ queryKey: ["search-patient-list"] });

      return res;
    });
  }
  const {
    data: patientInsuranceData,
    isLoading: isLoadingInsurance,
    isFetching: isFetchingInsurance,
  }: any = useGetInsuranceByPatientId(formik?.values?.patient?.value);

  return (
    <SideSheetDrawer
      open={disclosureHost.opened}
      onClose={closeAndResetForm}
      title={title || "Add Referral"}
      w="900px"
      footerActions={
        <Button variant="contained" onClick={formik.submitForm}>
          Save
        </Button>
      }
    >
      <NewPatient
        open={newPatientDisclosure.opened}
        onClose={newPatientDisclosure.close}
        onSubmit={addNewPatientHandler}
      />
      <NewReferringProvider
        open={newReferringProvider.opened}
        onClose={newReferringProvider.close}
        referralInOut={isReferralOut ? "Receiving" : "Referring"}
      />
      <form onSubmit={formik.handleSubmit}>
        <Grid container spacing={2}>
          {/* Patient */}
          <Grid size={isReferralOut === false && !hideAddPatient ? 3 : 4}>
            <PatientSelect
              required
              disabled={isEditMode || !!prefillData?.patient}
              label="Patient"
              placeholder="Search Patient"
              value={formik.values?.patient as any}
              onChange={(val) => formik.setFieldValue("patient", val)}
              error={formik.touched.patient && (formik.errors.patient as any)}
              patientSearchParam={{
                isReferral: !isReferralOut,
                pagination: DEF_PAGINATION,
              }}
            />
          </Grid>
          {isReferralOut === false && !hideAddPatient && (
            <Grid
              sx={{
                alignItems: "center",
              }}
              size={1}
            >
              <Tooltip title={"Add New Patient"}>
                <PersonAddIcon
                  sx={{ cursor: "pointer", color: "primary.main", mt: 0.5 }}
                  onClick={newPatientDisclosure.open}
                />
              </Tooltip>
            </Grid>
          )}
          {/* Referral From */}
          <Grid size={isReferralOut === false ? 3 : 4}>
            <ProviderSelect
              label="Referral From"
              required
              disabled={isEditMode}
              value={formik.values.referralFrom as any}
              onChange={(val) => formik.setFieldValue("referralFrom", val)}
              prefillListOption={formik.values.referralFrom as any}
              error={
                formik.touched.referralFrom &&
                (formik.errors.referralFrom as any)
              }
              providerSearchParam={{
                isReferral: !isReferralOut,
                pagination: DEF_PAGINATION,
              }}
            />
          </Grid>
          {isReferralOut === false && (
            <Grid
              sx={{
                alignItems: "center",
              }}
              size={1}
            >
              <Tooltip title={"Add New Provider"}>
                <PersonAddIcon
                  sx={{ cursor: "pointer", color: "primary.main", mt: 0.5 }}
                  onClick={newReferringProvider.open}
                />
              </Tooltip>
            </Grid>
          )}
          {/* Referral To */}
          <Grid size={isReferralOut === true ? 3 : 4}>
            <ProviderSelect
              label="Referral To"
              required
              disabled={isEditMode}
              value={formik.values.referralTo as any}
              onChange={(val) => formik.setFieldValue("referralTo", val)}
              prefillListOption={formik.values.referralTo as any}
              error={
                formik.touched.referralTo && (formik.errors.referralTo as any)
              }
              providerSearchParam={{
                isReferral: !!isReferralOut,
                pagination: DEF_PAGINATION,
              }}
            />
          </Grid>
          {isReferralOut === true && (
            <Grid
              sx={{
                alignItems: "center",
              }}
              size={1}
            >
              <Tooltip title={"Add New Provider"}>
                <PersonAddIcon
                  sx={{ cursor: "pointer", color: "primary.main", mt: 0.5 }}
                  onClick={newReferringProvider.open}
                />
              </Tooltip>
            </Grid>
          )}
          {/*Referral Speciality*/}
          <Grid size={4}>
            <AsyncSelect
              loading={isLoadingSpeciality || isRefetchingSpeciality}
              label="Referral Speciality"
              options={specialtiesList?.data?.content?.map((it) => ({
                value: it?.uuid,
                label: it?.name,
              }))}
              enableFilterOption
              renderCustomLabel={(option) => (
                <Flex flexDirection={"column"}>
                  <Text>{option.label}</Text>
                </Flex>
              )}
              onSearch={setSpecialitySearch}
              placeholder="Select Speciality"
              value={formik.values.referralSpecialities as any}
              onChange={(val) =>
                formik.setFieldValue("referralSpecialities", val)
              }
              error={
                formik.touched.referralSpecialities &&
                (formik.errors.referralSpecialities as any)
              }
            />
          </Grid>
          {/* Referral Date */}
          <Grid size={4}>
            <CustomDatePicker
              label="Referral Date"
              disabled={isEditMode}
              value={formik.values.referralDate}
              onChange={(val) => formik.setFieldValue("referralDate", val)}
              placeholder="Choose Date"
              required
              error={
                formik.touched.referralDate &&
                (formik.errors.referralDate as any)
              }
            />
          </Grid>
          {/* Priority */}
          <Grid size={4}>
            <CustomSelect
              label="Priority"
              options={REFERRAL_PRIORITIES}
              value={formik.values.referralPriority}
              onChange={(val) => formik.setFieldValue("referralPriority", val)}
              placeholder="Select"
              error={
                formik.touched.referralPriority &&
                (formik.errors.referralPriority as any)
              }
            />
          </Grid>
          {/* Row 3: Reason */}
          <Grid size={12}>
            <Input
              label="Referral Reason"
              textFieldProps={{
                placeholder: "Referral Reason",
                name: "referralReason",
                value: formik.values.referralReason,
                onChange: formik.handleChange,
              }}
            />
          </Grid>
          {/* Row 4: Note */}
          <Grid size={12}>
            <CustomTextArea
              label="Referral Note"
              placeholder="Referral Note"
              name="referralNote"
              value={formik.values.referralNote}
              onChange={formik.handleChange}
              minRows={4}
            />
          </Grid>
          {/* Row 5: Diagnosis & Insurance */}
          <Grid size={6}>
            <MaskMultiSelect
              label="Diagnosis Code"
              options={diagnosisList?.data?.content?.map((it) => ({
                value: it?.uuid,
                label: `${it?.code} - ${it?.description}`,
              }))}
              onSearch={setSearchCodeKey}
              placeholder="Search & Select Diagnosis Code"
              value={formik.values.referralDiagnosisCode as any}
              onChange={(val) =>
                formik.setFieldValue("referralDiagnosisCode", val)
              }
              error={
                formik.touched.referralDiagnosisCode &&
                (formik.errors.referralDiagnosisCode as any)
              }
            />
          </Grid>
          <Grid size={6}>
            <AsyncSelect
              loading={isLoadingInsurance || isFetchingInsurance}
              label="Insurance"
              options={patientInsuranceData?.content?.map((it: any) => ({
                value: it?.uuid,
                label: `${it?.insurancePayer?.payerName}`,
              }))}
              enableFilterOption
              renderCustomLabel={(option) => (
                <Flex flexDirection={"column"}>
                  <Text>{option.label}</Text>
                </Flex>
              )}
              onSearch={setSearchCodeKey}
              placeholder="Search & Select Insurance"
              value={formik.values.patientInsurance}
              onChange={(val) => formik.setFieldValue("patientInsurance", val)}
              error={
                formik.touched.patientInsurance &&
                (formik.errors.patientInsurance as any)
              }
            />
          </Grid>
          {/* Referral Appointment */}
          {(referringOutFromEncounter || formik.values.referralAppointment) && (
            <Grid size={6}>
              <AsyncSelect
                loading={isLoadingAppointmentList || isFetchingAppointmentList}
                label="Appointment"
                options={appointmentList?.data?.content?.map((it: any) => ({
                  value: it.uuid,
                  label: `${
                    it?.type ? replaceUnderScoreAddSpace(it?.type) : "-"
                  } ${formatDateTime(it.startTime)}`,
                }))}
                enableFilterOption
                renderCustomLabel={(option) => (
                  <Flex flexDirection={"column"}>
                    <Text>{option.label}</Text>
                  </Flex>
                )}
                onSearch={setSearchAppointmentKey}
                placeholder="Search & Select Appointment"
                value={formik.values.referralAppointment}
                onChange={(val) =>
                  formik.setFieldValue("referralAppointment", val)
                }
                error={
                  formik.touched.referralAppointment &&
                  (formik.errors.referralAppointment as any)
                }
              />
            </Grid>
          )}
          {/* Row 6: Upload Documents */}
          <Grid size={12}>
            {/* <div
              style={{
                border: "2px dashed #ccc",
                borderRadius: 8,
                padding: 24,
                textAlign: "center",
                marginTop: 8,
                marginBottom: 8,
              }}
            >
              <div style={{ fontSize: 32, color: "#aaa" }}>↑</div>
              <div>
                Drag & Drop files
                <br />
                Or{" "}
                <span style={{ color: "#1976d2", cursor: "pointer" }}>
                  Browse Files
                </span>
              </div>
              <div style={{ fontSize: 12, color: "#888", marginTop: 8 }}>
                Supported formats: JPEG, PNG, GIF, MP4, PDF, PSD, AI, Word, PPT
              </div>
            </div> */}
            <DropZone
              accept={".jpg,.jpeg,.png,.pdf"}
              label="Document"
              content="Upload file"
              height={"190px"}
              backgroundColor={"primary.light"}
              uploadIcon={
                <AddPhotoAlternateOutlinedIcon sx={{ fontSize: "4rem" }} />
              }
              value={formik.values.referralDocument}
              fileName={formik.values.referralDocumentName}
              onClick={async (files) => {
                if (files) {
                  const base64File = await convertToBase64(files[0]);
                  formik.setFieldValue("referralDocument", base64File);
                  formik.setFieldValue(
                    "referralDocumentName",
                    files[0].name ?? "attachment"
                  );
                } else {
                  formik.setFieldValue("referralDocument", null);
                  formik.setFieldValue("referralDocumentName", "");
                }
              }}
            />
          </Grid>
        </Grid>
      </form>
    </SideSheetDrawer>
  );
};

export default UpsertReferral;
